import { memo } from "react";
import { ResponsiveLine, SliceTooltipProps } from "@nivo/line";
import { Chip } from "@nivo/tooltip";
import { useTheme } from "@nivo/core";
import { NetIncomeAnalysis } from "../types";
import { schemeCategory10 } from "d3-scale-chromatic";

const colors = schemeCategory10; // or any other 10-color scale

// Format: "2024-12" → "Dec 2024"
const formatMonth = (month: string): string => {
  const [year, m] = month.split("-");
  const date = new Date(Number(year), Number(m) - 1);
  return date.toLocaleString("en-US", { month: "short", year: "numeric" });
};

const nivoTheme = {
  text: {
    color: "#8B8D97",
    fontSize: "13px",
    fontFamily: "Satoshi"
  },
  axis: {
    ticks: {
      line: { strokeWidth: 0 },
      text: {
        fill: "#8B8D97",
        fontSize: "13px",
        fontFamily: "Satoshi"
      }
    },
    domain: {
      line: { stroke: "rgba(28,29,34,0.100)" }
    }
  },
  tooltip: {
    chip: {
      borderRadius: "50%",
      marginRight: "0px",
      height: "8px",
      width: "8px"
    },
    tableCell: {
      fontSize: "13px",
      color: "#1C1D22"
    },
    container: {
      borderRadius: "4px"
    }
  },
  crosshair: {
    line: {
      stroke: "rgba(28,29,34,0.300)",
      strokeWidth: 1,
      strokeDasharray: "2 2"
    }
  },
  grid: {
    line: {
      stroke: "rgba(28,29,34,0.100)",
      strokeWidth: 1,
      strokeDasharray: "2 2"
    }
  }
};

const ChartTableTooltip = memo(({ slice, axis }: SliceTooltipProps) => {
  const theme = useTheme();
  const otherAxis = axis === "x" ? "y" : "x";

  const total = slice.points.reduce(
    (sum, point) => sum + (point.data[otherAxis] as number),
    0
  );

  return (
    <div style={theme.tooltip.container}>
      <table style={theme.tooltip.table} className="w-full">
        <tbody>
          {slice.points.map((point, i) => (
            <tr key={i}>
              <td style={theme.tooltip.tableCell}>
                <Chip color={point.serieColor} style={theme.tooltip.chip} />
              </td>
              <td style={theme.tooltip.tableCell}>{point.serieId}</td>
              <td
                style={theme.tooltip.tableCell}
                className="max-w-xs xl:max-w-sm 2xl:max-w-lg 3xl:max-w-xl"
              >
                {point.data[`${otherAxis}Formatted`]}
              </td>
            </tr>
          ))}
          <tr>
            <td />
            <td style={{ ...theme.tooltip.tableCell, fontWeight: 600 }}>
              Total
            </td>
            <td
              style={{ ...theme.tooltip.tableCell, fontWeight: 600 }}
              className="max-w-xs xl:max-w-sm 2xl:max-w-lg 3xl:max-w-xl"
            >
              {total.toLocaleString("en-US", {
                style: "currency",
                currency: "USD"
              })}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
});

ChartTableTooltip.displayName = "ChartTableTooltip";

export const NetIncomeChart = ({ data }: { data: NetIncomeAnalysis }) => {
  // Get all unique months
  const allMonths = Array.from(
    new Set(Object.values(data).flatMap(Object.keys))
  ).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  // Keep only the last 12 months
  const recentMonths = allMonths.slice(-12);

  // Normalize data to include all recent months
  const chartData = Object.entries(data).map(([company, months], index) => ({
    id: company,
    color: colors[index % colors.length],
    data: recentMonths.map((month) => {
      const rawData = months[month];
      let total;
      if (typeof rawData === "number") total = rawData;
      else if (typeof rawData === "object" && "total" in rawData)
        total = rawData.total;
      else total = 0;

      return {
        x: formatMonth(month), // e.g., "Dec 2024"
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- can be undefined since we search by key
        y: total ?? 0
      };
    })
  }));

  return (
    <div className="h-[400px] w-full mb-10">
      <div>
        {/* legends */}
        <div className="flex flex-wrap gap-4 mb-4">
          {chartData.map((serie) => (
            <div key={serie.id} className="flex items-center gap-2">
              <div
                style={{
                  backgroundColor: serie.color
                }}
                className="w-3 h-3 rounded-full"
              />
              <span className="text-sm">{serie.id}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Nivo Line Chart */}

      <ResponsiveLine
        data={chartData}
        theme={nivoTheme}
        animate
        enableArea
        enableSlices="x"
        sliceTooltip={(props) => <ChartTableTooltip {...props} />}
        margin={{ top: 10, right: 30, bottom: 50, left: 70 }}
        xScale={{ type: "point" }}
        yScale={{ type: "linear", stacked: true }}
        yFormat={(value) =>
          Number(value).toLocaleString("en-US", {
            style: "currency",
            currency: "USD"
          })
        }
        axisBottom={{
          tickRotation: recentMonths.length > 10 ? -30 : 0
        }}
        axisLeft={{
          tickValues: 3,
          format: (value) =>
            Number(value).toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0
            })
        }}
        enableGridX={false}
        gridYValues={5}
        pointSize={7}
        lineWidth={1}
        areaOpacity={0.05}
        colors={{ scheme: "category10" }}
      />
    </div>
  );
};
